import get from "lodash.get";

import {
  AnalyzeTinybirdResourceResponseBodyType,
  BaseTinybirdMetricsType,
  DatasetId,
  ExperienceFe,
  GraphConfig,
  GraphType,
  ProductGroupTableData,
  TimeSeriesData,
  VariationDto,
  VariationIdNameOrder,
} from "@intelligems/ig-types";
import {
  BarMetricData,
  HistogramMetricData,
  LineMetricData,
  SankeyMetricData,
  StackedAreaMetricData,
  StackedBarMetricData,
} from "@src/types/analytics";
import { genericSortByOrder, sortByOrder } from "@src/utils/sortings";
import { UseQueryResult } from "@tanstack/react-query";

export const barFormatterTb = (
  dashboardData: BaseTinybirdMetricsType[],
  config: GraphConfig,
  variations: VariationIdNameOrder[],
  metricFilterIndex?: number
): BarMetricData[] => {
  const formattedData = dashboardData.map((data: BaseTinybirdMetricsType) => {
    const foundVariation = variations.find(
      (group) => group.id === data["variation_id"]
    );
    const filterMethod = data[config.filterMetric!];
    const dataKey =
      config.metrics[
        config.hasMetricFilterIndex && metricFilterIndex ? metricFilterIndex : 0
      ];

    const metric = data[dataKey];

    return {
      title: config.title[0],
      value: metric?.value ?? 0,
      ci_high: metric?.ci_high ?? 0,
      ci_low: metric?.ci_low ?? 0,
      p2bb: metric?.p2bb ?? 0,
      p2bc: metric?.p2bc ?? 0,
      plus_minus: metric?.uplift?.plus_minus,
      order: foundVariation && foundVariation.order,
      filterMethod: filterMethod ? (filterMethod as string) : undefined,
      variation_id: data.variation_id,
      n_visitors: data["n_visitors"]?.value || 0,
      id: data.variation_id,
      name: variations.find((v) => v.id === data["variation_id"])?.name || "",
      uplift: metric?.uplift?.value,
      uplift_ci_high: metric?.uplift?.ci_high,
      uplift_ci_low: metric?.uplift?.ci_low,
      n_visitors_triggered: data[`n_visitors_${dataKey}`] ?? undefined,
    };
  });

  // Ensure there is an entry for every variation
  variations.forEach((variation) => {
    if (!formattedData.some((data) => data.variation_id === variation.id)) {
      formattedData.push({
        title: config.title[0],
        value: 0,
        ci_high: 0,
        ci_low: 0,
        p2bb: 0,
        p2bc: 0,
        plus_minus: 0,
        order: variation.order,
        filterMethod: undefined,
        variation_id: variation.id,
        n_visitors: 0,
        id: variation.id,
        name: variation.name,
        uplift: 0,
        uplift_ci_high: undefined,
        uplift_ci_low: undefined,
        n_visitors_triggered: undefined,
      });
    }
  });

  return formattedData.sort(genericSortByOrder);
};

export const sankeyConversionFunnelFormatterTb = (
  dashboardData: BaseTinybirdMetricsType[],
  config: GraphConfig,
  variations: Pick<VariationDto, "id" | "name">[],
  metricFilterIndex?: number,
  includeDidNotAddToCart: boolean = false
): SankeyMetricData[] => {
  return dashboardData.map((data: BaseTinybirdMetricsType) => {
    const convertedVisitors =
      (data.n_visitors?.value || 0) * (data.conversion_rate?.value || 0);
    if (includeDidNotAddToCart) {
      // Case with "All Visitors" included
      const nodes = [
        // 0 - Abandoned in Cart now at the top
        {
          name: "Abandoned in Cart",
          value:
            (data.n_visitors_added_to_cart?.value || 0) -
            (data.n_visitors_checkout_started?.value || 0),
        },
        // 1 - Abandoned in Checkout now second
        {
          name: "Abandoned in Checkout",
          value:
            (data.n_visitors_checkout_started?.value || 0) -
            data.n_visitors_ordered?.value,
        },
        // 2
        {
          name: "All Visitors",
          value: data.n_visitors?.value || 0,
        },
        // 3
        {
          name: "Did Not Add to Cart (hidden)",
          value:
            (data.n_visitors?.value || 0) -
            (data.n_visitors_added_to_cart?.value || 0),
        },
        // 4
        {
          name: "Did Not Add to Cart (hidden)",
          value:
            (data.n_visitors?.value || 0) -
            (data.n_visitors_added_to_cart?.value || 0),
        },
        // 5
        {
          name: "Did Not Add to Cart",
          value:
            (data.n_visitors?.value || 0) -
            (data.n_visitors_added_to_cart?.value || 0),
        },
        // 6
        {
          name: "Added to Cart",
          value: data.n_visitors_added_to_cart?.value || 0,
        },
        // 7
        {
          name: "Abandoned in Cart (hidden)",
          value:
            (data.n_visitors_added_to_cart?.value || 0) -
            (data.n_visitors_checkout_started?.value || 0),
        },
        // 8 - Began Checkout positioned earlier in the list
        {
          name: "Began Checkout",
          value: data.n_visitors_checkout_started?.value || 0,
        },
        // 9 - Hidden routing node for Abandoned Before Contact
        {
          name: "Abandoned Before Contact (hidden routing)",
          value:
            (data.n_visitors_checkout_started?.value || 0) -
            (data.n_visitors_checkout_contact_info_submitted?.value || 0),
        },
        // 10 - Contact Info positioned after the abandonment node
        {
          name: "Contact Info Submitted",
          value: data.n_visitors_checkout_contact_info_submitted?.value || 0,
        },
        // 11 - Hidden routing node
        {
          name: "Abandoned After Contact (hidden routing)",
          value:
            (data.n_visitors_checkout_contact_info_submitted?.value || 0) -
            (data.n_visitors_checkout_address_info_submitted?.value || 0),
        },
        // 12
        {
          name: "Address Info Submitted",
          value: data.n_visitors_checkout_address_info_submitted?.value || 0,
        },
        // 13 - Hidden routing node
        {
          name: "Abandoned After Address (hidden routing)",
          value:
            (data.n_visitors_checkout_address_info_submitted?.value || 0) -
            convertedVisitors,
        },
        // 14
        {
          name: "Converted",
          value: convertedVisitors,
        },
      ];

      const links = [
        // All Visitors to Did Not Add to Cart
        {
          source: 2,
          target: 3,
          value:
            (data.n_visitors?.value || 0) -
            (data.n_visitors_added_to_cart?.value || 0),
          rate: 1 - (data.add_to_cart_rate?.value || 0),
        },
        // All Visitors to Added to Cart
        {
          source: 2,
          target: 6,
          value: data.n_visitors_added_to_cart?.value || 0,
          rate: data.add_to_cart_rate?.value || 0,
        },
        // Added to Cart to Abandoned in Cart (hidden)
        {
          source: 6,
          target: 7,
          value:
            (data.n_visitors_added_to_cart?.value || 0) -
            (data.n_visitors_checkout_started?.value || 0),
          rate: 1 - (data.checkout_begin_rate?.value || 0),
        },
        // Abandoned in Cart (hidden) to Abandoned in Cart (now at the top)
        {
          source: 7,
          target: 0,
          value:
            (data.n_visitors_added_to_cart?.value || 0) -
            (data.n_visitors_checkout_started?.value || 0),
          rate: 1.0,
        },
        // Added to Cart to Began Checkout
        {
          source: 6,
          target: 8,
          value: data.n_visitors_checkout_started?.value || 0,
          rate: data.checkout_begin_rate?.value || 0,
        },
        // Began Checkout to Abandoned Before Contact (hidden routing) - This link will appear on top
        {
          source: 8,
          target: 9,
          value:
            (data.n_visitors_checkout_started?.value || 0) -
            (data.n_visitors_checkout_contact_info_submitted?.value || 0),
          rate:
            1 -
            (data.n_visitors_checkout_contact_info_submitted?.value || 0) /
              (data.n_visitors_checkout_started?.value || 1),
        },
        // Hidden routing node to Abandoned in Checkout (from above)
        {
          source: 9,
          target: 1,
          value:
            (data.n_visitors_checkout_started?.value || 0) -
            (data.n_visitors_checkout_contact_info_submitted?.value || 0),
          rate: 1.0,
        },
        // Began Checkout to Contact Info Submitted - This link will appear below
        {
          source: 8,
          target: 10,
          value: data.n_visitors_checkout_contact_info_submitted?.value || 0,
          rate:
            (data.n_visitors_checkout_contact_info_submitted?.value || 0) /
            (data.n_visitors_checkout_started?.value || 1),
        },
        // Contact Info Submitted to Address Info Submitted
        {
          source: 10,
          target: 12,
          value: data.n_visitors_checkout_address_info_submitted?.value || 0,
          rate:
            (data.n_visitors_checkout_address_info_submitted?.value || 0) /
            (data.n_visitors_checkout_contact_info_submitted?.value || 1),
        },
        // Contact Info Submitted to Abandoned After Contact (hidden routing)
        {
          source: 10,
          target: 11,
          value:
            (data.n_visitors_checkout_contact_info_submitted?.value || 0) -
            (data.n_visitors_checkout_address_info_submitted?.value || 0),
          rate:
            1 -
            (data.n_visitors_checkout_address_info_submitted?.value || 0) /
              (data.n_visitors_checkout_contact_info_submitted?.value || 1),
        },
        // Hidden routing node to Abandoned in Checkout (from above)
        {
          source: 11,
          target: 1,
          value:
            (data.n_visitors_checkout_contact_info_submitted?.value || 0) -
            (data.n_visitors_checkout_address_info_submitted?.value || 0),
          rate: 1.0,
        },
        // Address Info Submitted to Converted
        {
          source: 12,
          target: 14,
          value: convertedVisitors,
          rate:
            convertedVisitors /
            (data.n_visitors_checkout_address_info_submitted?.value || 1),
        },
        // Address Info Submitted to Abandoned After Address (hidden routing)
        {
          source: 12,
          target: 13,
          value:
            (data.n_visitors_checkout_address_info_submitted?.value || 0) -
            convertedVisitors,
          rate:
            1 -
            convertedVisitors /
              (data.n_visitors_checkout_address_info_submitted?.value || 1),
        },
        // Hidden routing node to Abandoned in Checkout (from above)
        {
          source: 13,
          target: 1,
          value:
            (data.n_visitors_checkout_address_info_submitted?.value || 0) -
            convertedVisitors,
          rate: 1.0,
        },
        // Did Not Add to Cart (hidden) to Did Not Add to Cart (hidden)
        {
          source: 3,
          target: 4,
          value:
            (data.n_visitors?.value || 0) -
            (data.n_visitors_added_to_cart?.value || 0),
          rate: 1.0,
        },
        // Did Not Add to Cart (hidden) to Did Not Add to Cart
        {
          source: 4,
          target: 5,
          value:
            (data.n_visitors?.value || 0) -
            (data.n_visitors_added_to_cart?.value || 0),
          rate: 1.0,
        },
      ];

      return {
        nodes,
        links,
        nVisitors: data.n_visitors?.value || 0,
        variation_id: data.variation_id,
      };
    } else {
      // Case without "All Visitors"
      const nodes = [
        // 0 - Abandoned in Cart now at the top
        {
          name: "Abandoned in Cart",
          value:
            (data.n_visitors_added_to_cart?.value || 0) -
            (data.n_visitors_checkout_started?.value || 0),
        },
        // 1 - Abandoned in Checkout now second
        {
          name: "Abandoned in Checkout",
          value:
            (data.n_visitors_checkout_started?.value || 0) - convertedVisitors,
        },
        // 2
        {
          name: "Abandoned in Cart (hidden)",
          value:
            (data.n_visitors_added_to_cart?.value || 0) -
            (data.n_visitors_checkout_started?.value || 0),
        },
        // 3
        {
          name: "Added to Cart",
          value: data.n_visitors_added_to_cart?.value || 0,
        },
        // 4 - Began Checkout positioned earlier in the list
        {
          name: "Began Checkout",
          value: data.n_visitors_checkout_started?.value || 0,
        },
        // 5 - Hidden routing node for Abandoned Before Contact
        {
          name: "Abandoned Before Contact (hidden routing)",
          value:
            (data.n_visitors_checkout_started?.value || 0) -
            (data.n_visitors_checkout_contact_info_submitted?.value || 0),
        },
        // 6 - Contact Info positioned after the abandonment node
        {
          name: "Contact Info Submitted",
          value: data.n_visitors_checkout_contact_info_submitted?.value || 0,
        },
        // 7 - Hidden routing node
        {
          name: "Abandoned After Contact (hidden routing)",
          value:
            (data.n_visitors_checkout_contact_info_submitted?.value || 0) -
            (data.n_visitors_checkout_address_info_submitted?.value || 0),
        },
        // 8
        {
          name: "Address Info Submitted",
          value: data.n_visitors_checkout_address_info_submitted?.value || 0,
        },
        // 9 - Hidden routing node
        {
          name: "Abandoned After Address (hidden routing)",
          value:
            (data.n_visitors_checkout_address_info_submitted?.value || 0) -
            convertedVisitors,
        },
        // 10
        {
          name: "Converted",
          value: convertedVisitors,
        },
      ];

      const links = [
        // Added to Cart to Began Checkout
        {
          source: 3,
          target: 4,
          value: data.n_visitors_checkout_started?.value || 0,
          rate: data.checkout_begin_rate?.value || 0,
        },
        // Added to Cart to Abandoned in Cart (hidden)
        {
          source: 3,
          target: 2,
          value:
            (data.n_visitors_added_to_cart?.value || 0) -
            (data.n_visitors_checkout_started?.value || 0),
          rate: 1 - (data.checkout_begin_rate?.value || 0),
        },
        // Abandoned in Cart (hidden) to Abandoned in Cart (now at the top)
        {
          source: 2,
          target: 0,
          value:
            (data.n_visitors_added_to_cart?.value || 0) -
            (data.n_visitors_checkout_started?.value || 0),
          rate: 1.0,
        },
        // Began Checkout to Abandoned Before Contact (hidden routing) - This link will appear on top
        {
          source: 4,
          target: 5,
          value:
            (data.n_visitors_checkout_started?.value || 0) -
            (data.n_visitors_checkout_contact_info_submitted?.value || 0),
          rate:
            1 -
            (data.n_visitors_checkout_contact_info_submitted?.value || 0) /
              (data.n_visitors_checkout_started?.value || 1),
        },
        // Hidden routing node to Abandoned in Checkout (from above)
        {
          source: 5,
          target: 1,
          value:
            (data.n_visitors_checkout_started?.value || 0) -
            (data.n_visitors_checkout_contact_info_submitted?.value || 0),
          rate: 1.0,
        },
        // Began Checkout to Contact Info Submitted - This link will appear below
        {
          source: 4,
          target: 6,
          value: data.n_visitors_checkout_contact_info_submitted?.value || 0,
          rate:
            (data.n_visitors_checkout_contact_info_submitted?.value || 0) /
            (data.n_visitors_checkout_started?.value || 1),
        },
        // Contact Info Submitted to Address Info Submitted
        {
          source: 6,
          target: 8,
          value: data.n_visitors_checkout_address_info_submitted?.value || 0,
          rate:
            (data.n_visitors_checkout_address_info_submitted?.value || 0) /
            (data.n_visitors_checkout_contact_info_submitted?.value || 1),
        },
        // Contact Info Submitted to Abandoned After Contact (hidden routing)
        {
          source: 6,
          target: 7,
          value:
            (data.n_visitors_checkout_contact_info_submitted?.value || 0) -
            (data.n_visitors_checkout_address_info_submitted?.value || 0),
          rate:
            1 -
            (data.n_visitors_checkout_address_info_submitted?.value || 0) /
              (data.n_visitors_checkout_contact_info_submitted?.value || 1),
        },
        // Hidden routing node to Abandoned in Checkout (from above)
        {
          source: 7,
          target: 1,
          value:
            (data.n_visitors_checkout_contact_info_submitted?.value || 0) -
            (data.n_visitors_checkout_address_info_submitted?.value || 0),
          rate: 1.0,
        },
        // Address Info Submitted to Converted
        {
          source: 8,
          target: 10,
          value: convertedVisitors,
          rate:
            convertedVisitors /
            (data.n_visitors_checkout_address_info_submitted?.value || 1),
        },
        // Address Info Submitted to Abandoned After Address (hidden routing)
        {
          source: 8,
          target: 9,
          value:
            (data.n_visitors_checkout_address_info_submitted?.value || 0) -
            convertedVisitors,
          rate:
            1 -
            convertedVisitors /
              (data.n_visitors_checkout_address_info_submitted?.value || 1),
        },
        // Hidden routing node to Abandoned in Checkout (from above)
        {
          source: 9,
          target: 1,
          value:
            (data.n_visitors_checkout_address_info_submitted?.value || 0) -
            convertedVisitors,
          rate: 1.0,
        },
      ];

      return {
        nodes,
        links,
        nVisitors: data.n_visitors?.value || 0,
        variation_id: data.variation_id,
      };
    }
  });
};

export const stackedHorizBarFormatterTb = (
  dashboardData: BaseTinybirdMetricsType[],
  config: GraphConfig,
  variations: VariationIdNameOrder[],
  metricFilterIndex?: number
): StackedBarMetricData[] => {
  const groupedByVariation: { [key: string]: any[] } = dashboardData.reduce(
    (acc, item) => {
      if (!acc[item.variation_id]) {
        acc[item.variation_id] = [];
      }
      acc[item.variation_id].push(item);
      return acc;
    },
    {}
  );

  // Ensure there is an entry for every variation
  variations.forEach((variation) => {
    if (!groupedByVariation[variation.id]) {
      groupedByVariation[variation.id] = [
        {
          variation_id: variation.id,
          n_orders: 0,
          net_revenue: 0,
          pct_orders_by_unit_quantity: null,
          pct_revenue_by_unit_quantity: null,
          unit_quantity: 0,
          net_product_revenue: 0,
          pct_total_revenue: 0,
          pct_total_units: 0,
          order: variation.order,
        },
      ];
    }
  });

  if (config.datasetId === "variation_product_groups") {
    const orderMap = new Map<string, number>();
    dashboardData.forEach((item) => {
      orderMap.set(item.product_group, item._order.value);
    });
    const allProductGroups = [
      ...new Set(
        dashboardData.map((item) => item[config.filterMetric || "variation_id"])
      ),
    ].sort((a, b) => {
      const orderA = orderMap.has(a) ? orderMap.get(a)! : Infinity;
      const orderB = orderMap.has(b) ? orderMap.get(b)! : Infinity;

      // Sort by order value
      return orderA - orderB;
    });

    // Transform data for each variation
    return Object.entries(groupedByVariation).map(([variationId, items]) => {
      // Create the base object with variation_id
      const formattedData: StackedBarMetricData = {
        variation_id: variationId,
        stackedHorizValues: {},
        id: variationId,
        order:
          variations.find((variation) => variation.id === variationId)?.order ||
          0,
      };
      allProductGroups.forEach((group) => {
        formattedData.stackedHorizValues![group] = 0;
      });

      // Add percentage for each unit quantity
      items.forEach((item) => {
        // Convert to percentage and round to 2 decimal places
        const percentage =
          item[
            config.metrics[
              config.hasMetricFilterIndex && metricFilterIndex
                ? metricFilterIndex
                : 0
            ]
          ]?.value * 100;

        if (
          config.maxGroupAmt &&
          item[config.filterMetric || "variation_id"] >= config.maxGroupAmt
        ) {
          // Combine 6+ units into one category
          formattedData.stackedHorizValues![`${config.maxGroupAmt}+`] =
            (formattedData.stackedHorizValues![`${config.maxGroupAmt}+`] || 0) +
            percentage;
        } else {
          formattedData.stackedHorizValues![
            `${item[config.filterMetric || "variation_id"]}`
          ] = percentage;
        }
      });

      return formattedData;
    });
  } else {
    // Transform data for each variation
    const allQuantityGroups = [
      ...new Set(
        dashboardData.map((item) => item[config.filterMetric || "variation_id"])
      ),
    ]
      .sort((a, b) => a - b)
      .filter((group) => !config.maxGroupAmt || group <= config.maxGroupAmt);
    return Object.entries(groupedByVariation)
      .map(([variationId, items]) => {
        // Sort items by unit_quantity to ensure consistent order
        if (config.filterMetric === "unit_quantity") {
          items.sort(
            (a, b) =>
              a[config.filterMetric || "variation_id"] -
              b[config.filterMetric || "variation_id"]
          );
        }

        // Create the base object with variation_id
        const formattedData: StackedBarMetricData = {
          variation_id: variationId,
          stackedHorizValues: {},
          id: variationId,
          order: variations.find((variation) => variation.id === variationId)
            ?.order,
        };

        allQuantityGroups.forEach((group) => {
          formattedData.stackedHorizValues![group] = 0;
        });

        // Add percentage for each unit quantity
        items.forEach((item) => {
          // Convert to percentage and round to 2 decimal places
          const percentage =
            Math.round(item[config.metrics[0]]?.value * 100 * 100) / 100;

          const unitQuantity = item[config.filterMetric || "variation_id"];

          if (config.maxGroupAmt && unitQuantity >= config.maxGroupAmt) {
            // Group all values >= maxGroupAmt together
            formattedData.stackedHorizValues![config.maxGroupAmt] =
              (formattedData.stackedHorizValues![config.maxGroupAmt] || 0) +
              percentage;
          } else {
            formattedData.stackedHorizValues![`${unitQuantity}`] = percentage;
          }
        });

        return formattedData;
      })
      .sort(genericSortByOrder);
  }
};
export const stackedBarFormatterTb = (
  dashboardData: BaseTinybirdMetricsType[],
  config: GraphConfig,
  variations: VariationIdNameOrder[]
): StackedBarMetricData[] => {
  // One stacked segment per legend title, mapped to the metric at the same
  // index. Handles any number of segments (2 for AOV, 3 for AOV + upsell, …).
  const legendTitles = config.stackedBarLegendTitles ?? [];
  const buildSegments = (source?: BaseTinybirdMetricsType) =>
    legendTitles.reduce<Record<string, number>>((acc, title, index) => {
      acc[title] = source?.[config.metrics[index]]?.value || 0;
      return acc;
    }, {});

  const formattedData = dashboardData.map((data: BaseTinybirdMetricsType) => {
    const testGroupMetrics = variations.find(
      (group) => group.id === data["variation_id"]
    );
    return {
      title: config.title[0],
      name: variations.find((v) => v.id === data["variation_id"])?.name || "",
      variation_id: data["variation_id"],
      order: testGroupMetrics ? testGroupMetrics?.order : 0,
      id: data["variation_id"],
      ids: legendTitles,
      ...buildSegments(data),
      impactMetricVal: config.impactMetric
        ? data[config.impactMetric]?.value
        : undefined,
      [config.denominatorMetric ?? "n_visitors"]:
        data[config.denominatorMetric ?? "n_visitors"]?.value,
      uplift: config.impactMetric
        ? data[config.impactMetric]?.uplift?.value || 0
        : undefined,
      plus_minus: config.impactMetric
        ? data[config.impactMetric]?.uplift?.plus_minus
        : undefined,
      uplift_ci_low: config.impactMetric
        ? data[config.impactMetric]?.uplift?.ci_low
        : undefined,
      uplift_ci_high: config.impactMetric
        ? data[config.impactMetric]?.uplift?.ci_high
        : undefined,
    };
  });

  // Ensure there is an entry for every variation
  variations.forEach((variation) => {
    if (!formattedData.some((data) => data.variation_id === variation.id)) {
      formattedData.push({
        title: config.title[0],
        name: variation.name,
        variation_id: variation.id,
        order: variation.order,
        id: variation.id,
        ids: legendTitles,
        ...buildSegments(),
        impactMetricVal: undefined,
        [config.denominatorMetric ?? "n_visitors"]: 0,
        uplift: 0,
        plus_minus: undefined,
        uplift_ci_low: null,
        uplift_ci_high: null,
      });
    }
  });

  return formattedData.sort(sortByOrder);
};

export const histogramFormatterTb = (
  dashboardData: BaseTinybirdMetricsType[],
  config: GraphConfig,
  variations: VariationIdNameOrder[]
): HistogramMetricData[] => {
  return dashboardData.map((data) => {
    const variationData = variations.reduce(
      (acc, variation) => ({
        ...acc,
        [variation.id]: data[variation.id],
      }),
      {}
    );
    return {
      title: config.title[0],
      name:
        variations?.find((v) => v.id === data["variation_id"])?.name ||
        ("" as string),
      bucket: data["bin"],
      ...variationData,
      bucketCount: data[config.metrics[0]] as number,
    };
  });
};

export const timeSeriesFormatter = (
  queryData: BaseTinybirdMetricsType[],
  selectedMetricId: string,
  filterMetric: string,
  graphInterval: string,
  locale?: string
) => {
  const formattedData: TimeSeriesData[] = [];
  const uniqueFilterValues = queryData
    .map((metric) => metric[filterMetric])
    .filter((val, id, array) => array.indexOf(val) === id)
    .sort();

  uniqueFilterValues.forEach((value) => {
    formattedData.push({ xAxisKey: value as string });
  });

  formattedData.forEach((data: TimeSeriesData, index: number) => {
    const groupsByFilterMetric = queryData.filter(
      (group) => group[filterMetric] === data.xAxisKey
    );
    groupsByFilterMetric.forEach((groupData: BaseTinybirdMetricsType) => {
      const selectedMetricData = groupData[selectedMetricId];
      if (!selectedMetricData) return; // skip if missing

      Object.assign(formattedData[index], {
        [groupData["variation_id"]]: selectedMetricData.value,
        [`${groupData["variation_id"]}-range`]: selectedMetricData.plus_minus,
        [`${groupData["variation_id"]}-interval`]: [
          selectedMetricData.ci_low,
          selectedMetricData.ci_high,
        ],
      });
    });
  });

  return formattedData.map((data: TimeSeriesData) => {
    return {
      ...data,
      xAxisKey: new Date(data.xAxisKey).toLocaleDateString(locale || "en-US", {
        month: "short",
        day: "numeric",
        hour: graphInterval === "HOUR" ? "numeric" : undefined,
        weekday: "short",
      }),
    };
  });
};

export const productGroupTableFormatter = (
  queryData: BaseTinybirdMetricsType[],
  selectedMetricId: string
) => {
  const formattedData: ProductGroupTableData[] = [];
  const orderMap = new Map<string, number>();
  queryData.forEach((item) => {
    orderMap.set(item.product_group, item._order.value);
  });
  const uniqueFilterValues = queryData
    .map((metric) => metric.product_group)
    .filter((val, id, array) => array.indexOf(val) === id && val !== "")
    .sort();

  uniqueFilterValues.forEach((value) => {
    formattedData.push({ productGroup: value as string });
  });

  formattedData.forEach((data: ProductGroupTableData, index: number) => {
    const groupsByFilterMetric = queryData.filter(
      (group) => group.product_group === data.productGroup
    );
    groupsByFilterMetric.forEach((groupData: BaseTinybirdMetricsType) => {
      Object.assign(formattedData[index], {
        [groupData["variation_id"]]: groupData[selectedMetricId].value,
      });
    });
  });

  return formattedData.sort((a, b) => {
    const orderA = orderMap.has(a.productGroup)
      ? orderMap.get(a.productGroup)!
      : Infinity;
    const orderB = orderMap.has(b.productGroup)
      ? orderMap.get(b.productGroup)!
      : Infinity;

    return orderA - orderB;
  });
};
export const lineFormatterTb = (
  dashboardData: BaseTinybirdMetricsType[],
  config: GraphConfig,
  locale?: string,
  interval?: string
): LineMetricData[] => {
  if (config.hasKDE || config.hasCDF) {
    return dashboardData.map((dashData) => ({
      ...dashData,
      xAxisKey: dashData["x"],
    }));
  } else {
    return timeSeriesFormatter(
      dashboardData,
      config.metrics[0],
      "dt",
      interval || "WEEK",
      locale
    );
  }
};

export const stackedAreaFormatterTb = (
  dashboardData: BaseTinybirdMetricsType[],
  config: GraphConfig,
  locale?: string,
  interval?: string
): StackedAreaMetricData[] => {
  if (config.hasKDE || config.hasCDF) {
    return dashboardData.map((dashData) => ({
      ...dashData,
      xAxisKey: dashData["x"],
    }));
  } else {
    const formattedData: StackedAreaMetricData[] = [];

    const uniqueFilterValues = dashboardData
      .map((metric) => metric[config.filterMetric!])
      .filter((val, id, array) => array.indexOf(val) === id)
      .sort();

    uniqueFilterValues.forEach((value) => {
      formattedData.push({ xAxisKey: value as string });
    });

    formattedData.forEach((data: StackedAreaMetricData, index: number) => {
      const groupsByFilterMetric = dashboardData.filter(
        (group: BaseTinybirdMetricsType) =>
          group[config.filterMetric!] === data.xAxisKey
      );
      groupsByFilterMetric.forEach((groupData: BaseTinybirdMetricsType) => {
        if (config.metrics.length === 1) {
          Object.assign(formattedData[index], {
            title: config.title,
            [groupData["variation_id"]]: groupData[config.metrics[0]]?.value,
          });
        } else {
          // sitewide
          Object.assign(formattedData[index], {
            title: config.title,
            [config.metrics[0]]: groupData[config.metrics[0]],
            [config.metrics[1]]: groupData[config.metrics[1]],
          });
        }
      });
    });

    return formattedData.map((data: StackedAreaMetricData) => {
      return {
        ...data,
        xAxisKey: new Date(data.xAxisKey).toLocaleDateString(
          locale || "en-US",
          {
            month: "short",
            day: "numeric",
            hour: interval === "HOUR" ? "numeric" : undefined,
            minute: interval === "HOUR" ? "numeric" : undefined,
          }
        ),
      };
    });
  }
};

export const filterDashboardDataTb = (
  dashboardData: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[],
  datasetId: DatasetId,
  config: GraphConfig
): BaseTinybirdMetricsType[] | undefined => {
  if (!dashboardData) {
    return undefined;
  }

  const filteredData =
    dashboardData.find((data) => {
      if (data.data) {
        if (data.data.datasetId === datasetId) {
          return true;
        }
      }
      return false;
    })?.data || [];
  if (config?.hasKDE) {
    return get(filteredData, "kde", undefined);
  } else if (config?.hasCDF) {
    return get(filteredData, "cdf", undefined);
  } else if (datasetId === "variation_histogram") {
    return get(filteredData, "histogram", undefined);
  } else {
    return get(filteredData, "metrics", undefined);
  }
};

export const selectData = ({
  dashboardData,
  config,
  _resource,
  locale,
  metricFilterIndex,
  variations,
  interval,
}: {
  dashboardData: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
  config: GraphConfig;
  _resource?: Pick<ExperienceFe, "id">;
  locale?: string;
  metricFilterIndex?: number;
  variations: VariationIdNameOrder[];
  interval?: string;
}):
  | BarMetricData[]
  | HistogramMetricData[]
  | StackedBarMetricData[]
  | StackedAreaMetricData[]
  | LineMetricData[]
  | SankeyMetricData[]
  | undefined => {
  const filteredDashboardData: BaseTinybirdMetricsType[] | undefined =
    filterDashboardDataTb(dashboardData, config?.datasetId, config);

  if (!filteredDashboardData || filteredDashboardData.length === 0) {
    return undefined;
  }
  if (
    !_resource &&
    (config.graphType === GraphType.StackedBar ||
      config.graphType === GraphType.ClusteredBar ||
      config.graphType === GraphType.BiaxialBar ||
      config.graphType === GraphType.Bar)
  ) {
    return undefined;
  }
  if (config.graphType === GraphType.Histogram) {
    return histogramFormatterTb(
      filteredDashboardData || [],
      config,
      variations
    );
  } else if (config.graphType === GraphType.Line) {
    return lineFormatterTb(
      filteredDashboardData || [],
      config,
      locale,
      interval
    );
  } else if (config.graphType === GraphType.StackedArea) {
    return stackedAreaFormatterTb(
      filteredDashboardData || [],
      config,
      locale,
      interval
    );
  } else if (
    config.graphType === GraphType.StackedBar ||
    config.graphType === GraphType.ClusteredBar ||
    config.graphType === GraphType.BiaxialBar
  ) {
    return stackedBarFormatterTb(
      filteredDashboardData || [],
      config,
      variations
    );
  } else if (config.graphType === GraphType.StackedBarHorizontal) {
    return stackedHorizBarFormatterTb(
      filteredDashboardData || [],
      config,
      variations,
      metricFilterIndex
    );
  } else if (config.graphType === GraphType.Sankey) {
    return sankeyConversionFunnelFormatterTb(
      filteredDashboardData,
      config,
      variations,
      metricFilterIndex
    );
  } else {
    return barFormatterTb(
      filteredDashboardData,
      config,
      variations,
      metricFilterIndex
    );
  }
};
